import React, { useEffect, useRef, useState } from "react";
import "./AudioPlayer.css";

interface AudioPlayerProps {
  src: string;
}

const formatTime = (seconds: number): string => {
  if (!isFinite(seconds) || seconds < 0) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60)
    .toString()
    .padStart(2, "0");
  return `${m}:${s}`;
};

const SPEED_OPTIONS = [0.5, 0.75, 1, 1.25, 1.5, 2];

const AudioPlayer: React.FC<AudioPlayerProps> = ({ src }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [hasError, setHasError] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showSpeedOptions, setShowSpeedOptions] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);

  // Reseta o estado visual (incluindo erro) sempre que a ave selecionada muda (novo src)
  useEffect(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setHasError(false);
    setShowMenu(false);
    setShowSpeedOptions(false);
    setPlaybackRate(1);
  }, [src]);

  // Fecha o menu ao clicar fora dele
  useEffect(() => {
    if (!showMenu) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
        setShowSpeedOptions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showMenu]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio || hasError) return;
    if (isPlaying) {
      audio.pause();
    } else {
      // audio.play() retorna uma Promise que rejeita se o arquivo não
      // carregar (404, formato inválido, etc.) — sem o .catch() isso
      // vira um "Uncaught runtime error" não tratado.
      audio.play().catch(() => setHasError(true));
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio || hasError) return;
    const time = Number(e.target.value);
    audio.currentTime = time;
    setCurrentTime(time);
  };

  const handleSpeedChange = (rate: number) => {
    const audio = audioRef.current;
    if (audio) {
      audio.playbackRate = rate;
    }
    setPlaybackRate(rate);
  };

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = src;
    link.download = src.split("/").pop() || "audio";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setShowMenu(false);
  };

  const progressPercent = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div className={`audio-player${hasError ? " audio-player-error" : ""}`}>
      {/* Elemento nativo escondido: só fornece os dados/controle real do áudio */}
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        style={{ display: "none" }}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
        onLoadedMetadata={(e) => {
          setDuration(e.currentTarget.duration);
          e.currentTarget.playbackRate = playbackRate;
        }}
        onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
        onError={() => setHasError(true)}
      />

      {hasError ? (
        <span className="audio-player-error-message">
          Áudio indisponível no momento
        </span>
      ) : (
        <>
          <button
            type="button"
            className="audio-player-toggle"
            onClick={togglePlay}
            aria-label={isPlaying ? "Pausar" : "Reproduzir"}
          >
            {isPlaying ? (
              <svg viewBox="0 0 24 24" fill="currentColor">
                <rect x="6" y="5" width="4" height="14" rx="1" />
                <rect x="14" y="5" width="4" height="14" rx="1" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>

          <span className="audio-player-time">{formatTime(currentTime)}</span>

          <input
            type="range"
            className="audio-player-seek"
            min={0}
            max={duration || 0}
            step={0.01}
            value={currentTime}
            onChange={handleSeek}
            style={{ ["--progress" as string]: `${progressPercent}%` } as React.CSSProperties}
            aria-label="Progresso do áudio"
          />

          <span className="audio-player-time">{formatTime(duration)}</span>

          <div className="audio-player-menu-wrapper" ref={menuRef}>
            <button
              type="button"
              className="audio-player-menu-btn"
              onClick={() => setShowMenu((prev) => !prev)}
              aria-label="Opções de áudio"
              aria-expanded={showMenu}
            >
              <i className="fi fi-bs-menu-dots-vertical" />
            </button>

            {showMenu && (
              <div className="audio-player-menu">
                <button
                  type="button"
                  className="audio-player-menu-item"
                  onClick={() => setShowSpeedOptions((prev) => !prev)}
                  aria-expanded={showSpeedOptions}
                >
                  <i className="fi fi-bs-player-speed" />
                  <span>Velocidade</span>
                  <span className="audio-player-menu-item-value">
                    {playbackRate}x
                  </span>
                </button>

                {showSpeedOptions && (
                  <div className="audio-player-speed-options">
                    {SPEED_OPTIONS.map((rate) => (
                      <button
                        key={rate}
                        type="button"
                        className={`audio-player-speed-btn${
                          playbackRate === rate ? " active" : ""
                        }`}
                        onClick={() => handleSpeedChange(rate)}
                      >
                        {rate}x
                      </button>
                    ))}
                  </div>
                )}

                <button
                  type="button"
                  className="audio-player-menu-item"
                  onClick={handleDownload}
                >
                  <span>Baixar áudio</span>
                  <i className="fi fi-bs-download" />
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AudioPlayer;